import React, { useCallback, useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  TableContainer,
  TablePagination,
  TableSortLabel,
  Box,
  Skeleton,
  Snackbar,
  Alert,
  Typography,
  Button,
  useTheme,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchTemplates, updateSelectedTemplate } from "../../redux/Slice/Templates/templateSlice";
import { setSelectedOption } from "../../redux/Slice/Settings/settingsSlice";
import { useNavigate, useParams } from "react-router-dom";
import NoChannelIllustration from "../../Images/Templates/no-channel-connected.svg";

const TemplatesTable = ({ searchQuery, category, language, status }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { accountid } = useParams();
  const { user } = useSelector((state) => state.auth);
  // Same signal Dashboard.jsx/ChatBody.jsx use to decide whether any channel is connected -
  // an empty template list doesn't by itself mean no channel is connected.
  const currentWorkspace = useSelector((state) => state.workspace?.currentWorkspace);
  const isChannelConnected = Boolean(
    currentWorkspace?.whatsappConnected ||
      currentWorkspace?.instagramConnected ||
      currentWorkspace?.facebookConnected
  );

  /** Search hits: dark = dark text on amber chip (readable); light = theme green on soft yellow */
  const highlightText = useCallback(
    (text, search) => {
      if (!search || text == null) return text;
      const raw = String(text);
      const escaped = search.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const splitRe = new RegExp(`(${escaped})`, "gi");
      const matchTest = new RegExp(`^${escaped}$`, "i");
      const parts = raw.split(splitRe);
      const isDark = theme.palette.mode === "dark";
      const matchStyle = isDark
        ? {
            backgroundColor: "#fcd34d",
            color: "#0f172a",
            fontWeight: 700,
            borderRadius: "3px",
            padding: "0 2px",
          }
        : {
            backgroundColor: "#fef9c3",
            color: theme.palette.primary.main,
            fontWeight: 700,
            borderRadius: "3px",
            padding: "0 2px",
          };

      return parts.map((part, i) => {
        if (part === "") return null;
        return matchTest.test(part) ? (
          <mark key={i} style={matchStyle}>
            {part}
          </mark>
        ) : (
          part
        );
      });
    },
    [theme.palette.mode, theme.palette.primary.main]
  );

  // Grab table data and status from Redux
  const {
    templates,
    status: fetchStatus,
    error,
  } = useSelector((state) => state.template);

  // For controlling table sorting, pagination
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10); // Default to 10

  // For controlling the Snackbar (error display)
  const [openSnackbar, setOpenSnackbar] = useState(false);

  useEffect(() => {
    dispatch(fetchTemplates());
  }, [dispatch]);

  // If status becomes "failed", show Snackbar
  useEffect(() => {
    if (fetchStatus === "failed") {
      setOpenSnackbar(true);
    }
  }, [fetchStatus]);

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter templates based on search query and filters
  const filteredTemplates = templates.filter((template) => {
    const matchesSearch = searchQuery
      ? template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.components.some((comp) =>
          comp.text?.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : true;

    const matchesCategory =
      category === "All" || template.category === category;
    const matchesLanguage =
      language === "All" || template.language === language;
    const matchesStatus = status === "All" || template.status === status;

    return matchesSearch && matchesCategory && matchesLanguage && matchesStatus;
  });

  // Sort filtered templates
  const sortedTemplates = [...filteredTemplates].sort((a, b) => {
    if (orderBy === "name") {
      return order === "asc"
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name);
    }
    return 0;
  });

  // Paginate
  const paginatedTemplates = sortedTemplates.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [searchQuery, category, language, status]);

  // 1) If loading, show skeleton
  if (fetchStatus === "loading") {
    return (
      <Box sx={{ padding: { xs: 1, sm: 1.5, md: 2, lg: 2, xl: 2 } }}>
        {/* Show multiple skeleton rows to mimic a table */}
        {[...Array(5)].map((_, idx) => (
          <Skeleton
            key={idx}
            variant="rectangular"
            height={{ xs: 35, sm: 38, md: 40, lg: 40, xl: 40 }}
            sx={{ mb: { xs: 1, sm: 1.5, md: 2, lg: 2, xl: 2 }, borderRadius: 1 }}
          />
        ))}
      </Box>
    );
  }

  // 2) No templates at all (not just filtered out) — same empty-state pattern as Inbox
  if (templates.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          py: 6,
          px: 4,
        }}
      >
        <Box
          component="img"
          src={NoChannelIllustration}
          alt=""
          sx={{ width: { xs: 150, sm: 180 }, height: "auto", mb: 1.5 }}
        />
        <Typography sx={{ fontWeight: 700, fontSize: { xs: 17, sm: 19 }, color: "text.primary", mb: 0.5 }}>
          {isChannelConnected ? "No templates yet" : "No channel connected"}
        </Typography>
        <Typography sx={{ fontSize: { xs: 13, sm: 14 }, color: "text.secondary", textAlign: "center", mb: 2 }}>
          {isChannelConnected
            ? "Create your first template to start sending it."
            : "Connect a channel to start creating templates."}
        </Typography>
        <Button
          variant="contained"
          onClick={() => {
            if (isChannelConnected) {
              navigate(`/home/${accountid}/templates/create`);
            } else {
              dispatch(setSelectedOption("Integrations"));
              navigate(`/home/${accountid}/settings`);
            }
          }}
          sx={{
            px: 2.5,
            py: 1,
            backgroundColor: (theme) => (theme.palette.mode === "dark" ? "#4FC389" : "#44B57C"),
            color: "#ffffff",
            borderRadius: "12px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.9rem",
            fontFamily: "Instrument Sans, sans-serif",
            boxShadow: (theme) => (theme.palette.mode === "dark" ? "none" : "0 2px 8px rgba(68,181,124,0.25)"),
            transition: "all 0.3s ease",
            "&:hover": {
              backgroundColor: (theme) => (theme.palette.mode === "dark" ? "#3aad77" : "#38a06c"),
              boxShadow: (theme) => (theme.palette.mode === "dark" ? "none" : "0 8px 20px rgba(68,181,124,0.35)"),
            },
          }}
        >
          {isChannelConnected ? "Create Template" : "Connect Channel"}
        </Button>
      </Box>
    );
  }

  // 3) Otherwise, show the table (including "idle" or "succeeded")
  return (
    <>
      <Paper
        sx={{
          width: "100%",
          overflow: "hidden",
          // xs/sm: fill flex parent so table + pagination stay above BottomNav; md+ unchanged
          height: { xs: "100%", sm: "100%", md: "100%", lg: "100%", xl: "100%" },
          maxHeight: { xs: "100%", sm: "100%", md: "100%", lg: "100%", xl: "100%" },
          flex: { xs: 1, sm: 1, md: 1, lg: 1, xl: 1 },
          minHeight: { xs: 0, sm: 0, md: 0, lg: 0, xl: 0 },
          backgroundColor: (theme) => theme.palette.background.paper,
          padding: { xs: 0, sm: 0, md: 1.5, lg: 2, xl: 0 }, // Remove all padding for xs/sm to prevent white space
          paddingTop: { xs: "2px", sm: "4px", md: 1.5, lg: 2, xl: 0 }, // Minimal top padding for xs/sm
          paddingLeft: { xs: "2px", sm: "4px", md: 1.5, lg: 2, xl: 0 }, // Minimal left padding for xs/sm
          paddingRight: { xs: "2px", sm: "4px", md: 1.5, lg: 2, xl: 0 }, // Minimal right padding for xs/sm
          paddingBottom: { xs: 0, sm: 0, md: 0, lg: 0, xl: 0 }, // No bottom padding to prevent white space
          marginBottom: { xs: 0, sm: 0, md: 0, lg: 0, xl: 0 }, // No margin to prevent white space
          marginTop: { xs: 0, sm: 0, md: 0, lg: 0, xl: 0 }, // No top margin to prevent white space
          display: "flex",
          flexDirection: "column",
          flexShrink: { xs: 1, sm: 1, md: 1, lg: 1, xl: 1 },
          alignItems: { xs: "stretch", sm: "stretch", md: "stretch", lg: "stretch", xl: "stretch" }, // Stretch to fill container
          justifyContent: { xs: "flex-start", sm: "flex-start", md: "flex-start", lg: "flex-start", xl: "flex-start" }, // Align to start
        }}
      >
        <TableContainer 
          sx={{ 
            flex: { xs: 1, sm: 1, md: 1, lg: 1, xl: 1 },
            minHeight: 0,
            maxHeight: { xs: "none", sm: "none", md: "100%", lg: "100%", xl: "100%" },
            overflowX: "auto",
            overflowY: "auto",
            marginBottom: { xs: 0, sm: 0, md: 0, lg: 0, xl: 0 }, // No margin to prevent white space
            // Hide scrollbar but keep scrolling functionality for xs and sm
            scrollbarWidth: { xs: "none", sm: "none", md: "auto", lg: "auto", xl: "auto" }, // Firefox
            msOverflowStyle: { xs: "none", sm: "none", md: "auto", lg: "auto", xl: "auto" }, // IE and Edge (old)
            "&::-webkit-scrollbar": {
              width: { xs: 0, sm: 0, md: "8px", lg: "8px", xl: "8px" }, // Hide scrollbar on xs and sm
              height: { xs: 0, sm: 0, md: "8px", lg: "8px", xl: "8px" },
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: (theme) => theme.palette.mode === "dark" ? "#1a1a1a" : "#f1f1f1",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: (theme) => theme.palette.mode === "dark" ? "#555" : "#888",
              borderRadius: "10px",
              "&:hover": {
                backgroundColor: (theme) => theme.palette.mode === "dark" ? "#777" : "#555",
              },
            },
          }}
        >
          <Table stickyHeader sx={{ minWidth: { xs: 600, sm: 650, md: 700, lg: 750, xl: 750 } }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: (theme) => theme.palette.background.paper,
                    padding: { xs: "6px 2px", sm: "8px 4px", md: "12px 8px", lg: "16px 16px", xl: "16px 16px" },
                    borderBottom: (theme) =>
                      `1px solid ${theme.palette.divider}`,
                    fontSize: { xs: "12px", sm: "11px", md: "12px", lg: "14px", xl: "14px" },
                    fontWeight: "bold",
                  }}
                >
                  <TableSortLabel
                    active={orderBy === "name"}
                    direction={orderBy === "name" ? order : "asc"}
                    onClick={() => handleRequestSort("name")}
                    sx={{
                      fontSize: "inherit",
                      "& .MuiTableSortLabel-icon": {
                        fontSize: { xs: "14px", sm: "16px", md: "18px", lg: "18px", xl: "18px" },
                      },
                    }}
                  >
                    Template Name
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: (theme) => theme.palette.background.paper,
                    padding: { xs: "6px 2px", sm: "8px 4px", md: "12px 8px", lg: "16px 16px", xl: "16px 16px" },
                    borderBottom: (theme) =>
                      `1px solid ${theme.palette.divider}`,
                    fontSize: { xs: "12px", sm: "11px", md: "12px", lg: "14px", xl: "14px" },
                    fontWeight: "bold",
                  }}
                >
                  Category
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: (theme) => theme.palette.background.paper,
                    padding: { xs: "6px 2px", sm: "8px 4px", md: "12px 8px", lg: "16px 16px", xl: "16px 16px" },
                    borderBottom: (theme) =>
                      `1px solid ${theme.palette.divider}`,
                    fontSize: { xs: "12px", sm: "11px", md: "12px", lg: "14px", xl: "14px" },
                    fontWeight: "bold",
                  }}
                >
                  Language
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: (theme) => theme.palette.background.paper,
                    padding: { xs: "6px 2px", sm: "8px 4px", md: "12px 8px", lg: "16px 16px", xl: "16px 16px" },
                    borderBottom: (theme) =>
                      `1px solid ${theme.palette.divider}`,
                    fontSize: { xs: "12px", sm: "11px", md: "12px", lg: "14px", xl: "14px" },
                    fontWeight: "bold",
                  }}
                >
                  Status
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: (theme) => theme.palette.background.paper,
                    padding: { xs: "6px 2px", sm: "8px 4px", md: "12px 8px", lg: "16px 16px", xl: "16px 16px" },
                    borderBottom: (theme) =>
                      `1px solid ${theme.palette.divider}`,
                    fontSize: { xs: "12px", sm: "11px", md: "12px", lg: "14px", xl: "14px" },
                    fontWeight: "bold",
                  }}
                >
                  Preview
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedTemplates.length > 0 ? (
                paginatedTemplates.map((template, index) => (
                  <TableRow
                    key={template.templateId || template.id || template.name}
                    hover
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      dispatch(updateSelectedTemplate(template));
                      navigate(`/home/${user?.accountId}/templates/analytics/${template.templateId || template.id || template.name}`, {
                        state: { templateName: template.name }
                      });
                    }}
                  >
                    <TableCell
                      sx={{
                        color: (theme) =>
                          theme.palette.mode === "dark"
                            ? theme.palette.text.primary
                            : theme.palette.primary.main,
                        padding: { xs: "12px 8px", sm: "6px 4px", md: "12px 8px", lg: "16px 16px", xl: "16px 16px" },
                        borderBottom: (theme) =>
                          `1px solid ${theme.palette.divider}`,
                        fontSize: { xs: "14px", sm: "14px", md: "15px", lg: "16px", xl: "17px" },
                        maxWidth: { xs: "120px", sm: "150px", md: "200px", lg: "none", xl: "none" },
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: { xs: "nowrap", sm: "nowrap", md: "normal", lg: "normal", xl: "normal" },
                      }}
                    >
                      {highlightText(template.name, searchQuery)}
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: { xs: "4px 2px", sm: "6px 4px", md: "12px 8px", lg: "16px 16px", xl: "16px 16px" },
                        borderBottom: (theme) =>
                          `1px solid ${theme.palette.divider}`,
                        fontSize: { xs: "10px", sm: "11px", md: "12px", lg: "14px", xl: "14px" },
                      }}
                    >
                      {template.category}
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: { xs: "4px 2px", sm: "6px 4px", md: "12px 8px", lg: "16px 16px", xl: "16px 16px" },
                        borderBottom: (theme) =>
                          `1px solid ${theme.palette.divider}`,
                        fontSize: { xs: "10px", sm: "11px", md: "12px", lg: "14px", xl: "14px" },
                      }}
                    >
                      {template.language}
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: { xs: "4px 2px", sm: "6px 4px", md: "12px 8px", lg: "16px 16px", xl: "16px 16px" },
                        borderBottom: (theme) =>
                          `1px solid ${theme.palette.divider}`,
                        fontSize: { xs: "10px", sm: "11px", md: "12px", lg: "14px", xl: "14px" },
                      }}
                    >
                      {template.status}
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: { xs: "4px 2px", sm: "6px 4px", md: "12px 8px", lg: "16px 16px", xl: "16px 16px" },
                        borderBottom: (theme) =>
                          `1px solid ${theme.palette.divider}`,
                        fontSize: { xs: "11px", sm: "10px", md: "11px", lg: "14px", xl: "14px" },
                        maxWidth: { xs: "150px", sm: "200px", md: "250px", lg: "none", xl: "none" },
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: { xs: "nowrap", sm: "nowrap", md: "normal", lg: "normal", xl: "normal" },
                      }}
                    >
                      {highlightText(
                        template.components[0].type === "HEADER"
                          ? template.components[1]?.text || ""
                          : template.components[0]?.text || "",
                        searchQuery
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    align="center"
                    sx={{
                      padding: { xs: "12px 4px", sm: "14px 6px", md: "16px 8px", lg: "16px 8px", xl: "16px 8px" },
                      borderBottom: (theme) =>
                        `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography 
                      variant="body1" 
                      color="textSecondary"
                      sx={{
                        fontSize: { xs: "11px", sm: "12px", md: "14px", lg: "14px", xl: "14px" },
                      }}
                    >
                      No templates found matching your search criteria
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 15, 20, 25]}
          component="div"
          count={filteredTemplates.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            position: "relative", // Relative for all sizes to ensure it's visible
            bottom: "auto", // Auto for all sizes
            zIndex: 1,
            backgroundColor: (theme) => theme.palette.background.paper,
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            borderBottom: "none", // Remove any bottom border
            color: (theme) => theme.palette.text.primary,
            fontSize: { xs: "7px", sm: "8px", md: "12px", lg: "14px", xl: "14px" }, // Reduced base font size for xs/sm
            overflow: "hidden", // Hide overflow to prevent white space
            overflowX: "hidden",
            overflowY: "hidden",
            marginTop: 0, // No extra margin
            marginBottom: { xs: 0, sm: 0, md: 0, lg: 0, xl: 0 }, // No margin to prevent white space
            // xs/sm: outlet + flex layout reserve BottomNav; extra padding hid pagination
            paddingBottom: { xs: 0, sm: 0, md: 0, lg: 0, xl: 0 },
            paddingTop: { xs: 0, sm: 0, md: 0, lg: 0, xl: 0 }, // No top padding
            flexShrink: 0, // Don't shrink pagination
            height: { xs: "fit-content", sm: "fit-content", md: "auto", lg: "auto", xl: "auto" }, // fit-content for xs/sm to prevent white space
            minHeight: { xs: "auto", sm: "auto", md: "auto", lg: "auto", xl: "auto" }, // Auto minHeight
            maxHeight: { xs: "none", sm: "none", md: "none", lg: "none", xl: "none" }, // No maxHeight constraint
            boxSizing: "border-box", // Ensure padding is included in height
            "& .MuiTablePagination-select": {
              color: (theme) => theme.palette.text.primary,
              fontSize: { xs: "7px", sm: "8px", md: "12px", lg: "14px", xl: "14px" }, // Reduced font size for xs/sm
              // md/lg: tighter vertical padding than xl (compact footer on desktop)
              padding: { xs: "1px 12px 1px 2px", sm: "2px 16px 2px 4px", md: "2px 28px 2px 12px", lg: "2px 28px 2px 12px", xl: "6px 32px 6px 14px" },
            },
            "& .MuiTablePagination-selectIcon": {
              color: (theme) => theme.palette.text.secondary,
              fontSize: { xs: "10px", sm: "12px", md: "20px", lg: "20px", xl: "20px" }, // Reduced icon size for xs/sm
            },
            "& .MuiTablePagination-displayedRows": {
              color: (theme) => theme.palette.text.secondary,
              fontSize: { xs: "7px", sm: "8px", md: "12px", lg: "14px", xl: "14px" }, // Reduced font size for xs/sm
            },
            "& .MuiTablePagination-toolbar": {
              minHeight: { xs: "28px", sm: "32px", md: "36px", lg: "36px", xl: "52px" },
              paddingLeft: { xs: "2px", sm: "4px", md: "12px", lg: "16px", xl: "16px" }, // Reduced padding for xs/sm
              paddingRight: { xs: "2px", sm: "4px", md: "12px", lg: "16px", xl: "16px" }, // Reduced padding for xs/sm
              paddingTop: { xs: "1px", sm: "2px", md: "2px", lg: "2px", xl: "8px" },
              paddingBottom: { xs: "1px", sm: "2px", md: "2px", lg: "2px", xl: "8px" },
              flexWrap: { xs: "wrap", sm: "wrap", md: "nowrap", lg: "nowrap", xl: "nowrap" },
              margin: 0, // Remove any default margins
              backgroundColor: "transparent", // Ensure no white background
            },
            "& .MuiTablePagination-spacer": {
              flex: { xs: "0 0 0", sm: "0 0 0", md: "1 1 100%", lg: "1 1 100%", xl: "1 1 100%" },
            },
            "& .MuiIconButton-root": {
              color: (theme) => theme.palette.text.primary,
              padding: { xs: "1px", sm: "2px", md: "4px", lg: "4px", xl: "8px" },
              "& .MuiSvgIcon-root": {
                fontSize: { xs: "12px", sm: "14px", md: "24px", lg: "24px", xl: "24px" }, // Reduced icon size for xs/sm
              },
            },
            "& .Mui-disabled": {
              color: (theme) => theme.palette.text.disabled,
            },
          }}
        />
      </Paper>

      {/* Snackbar for any error (shown if status === 'failed') */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          sx={{ width: "100%" }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
            {typeof error === "string" 
              ? error 
              : error?.error || error?.message || error?.details?.error || JSON.stringify(error) || "An error occurred while fetching templates."}
          </Typography>
          {(typeof error === "string" && error.includes("Invalid WhatsApp API credentials")) || 
           (typeof error === "object" && (error?.error?.includes("Invalid") || error?.message?.includes("Invalid WhatsApp"))) ? (
            <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
              Please edit your workspace and update the userAccessToken with a valid WhatsApp Business API access token.
            </Typography>
          ) : null}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TemplatesTable;
